import { HttpClient, HttpEventType } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { takeWhile } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserSimulationService } from './user-simulation.service';

export const testDataKinds = ['clients', 'users', 'sites', 'projects', 'periods', 'tasks', 'schedules'] as const;
export type TestDataKind = typeof testDataKinds[number];
export interface TestDataProgress {
  testDataBatchId?: string;
  kind?: TestDataKind;
  completed?: number;
  total?: number;
  created?: Record<string, string[]>;
  errors?: { kind?: TestDataKind; index?: number; code: string }[];
  done?: boolean;
  failed?: boolean;
}
@Injectable({ providedIn: 'root' })
export class TestDataService {
  private readonly http = inject(HttpClient);
  readonly available = inject(UserSimulationService).available;
  readonly running = signal(false);
  readonly progress = signal<TestDataProgress | null>(null);

  generate(contractor: string, scenario: string, counts: Record<TestDataKind, number>, randomStatuses: Partial<Record<TestDataKind, boolean>>) {
    if (!this.available() || this.running()) {
      return;
    }
    this.running.set(true);
    this.progress.set({});
    this.http.post(`${environment.backendUrl}/test-data`, { contractor, scenario, counts, randomStatuses }, {
      observe: 'events', responseType: 'text', reportProgress: true,
    }).pipe(
      // The shared auth interceptor stays subscribed; finish after the final HTTP response.
      takeWhile(event => event.type !== HttpEventType.Response, true),
    ).subscribe({
      next: event => {
        const text = event.type === HttpEventType.DownloadProgress ? event.partialText : event.type === HttpEventType.Response ? event.body : null;
        if (text) {
          const lines = text.split('\n').filter(line => line.trim());
          // Download events can end mid-record. Consume the latest complete record only.
          for (let index = lines.length - 1; index >= 0; index--) {
            try {
              const progress = JSON.parse(lines[index]) as TestDataProgress;
              this.progress.update(previous => ({ ...previous, ...progress }));
              break;
            } catch { /* Wait for the rest of the record. */ }
          }
        }
      },
      error: () => {
        this.progress.update(value => ({ ...value, failed: true }));
        this.running.set(false);
      },
      complete: () => {
        if (!this.progress()?.done) {
          this.progress.update(value => ({ ...value, failed: true }));
        }
        this.running.set(false);
      },
    });
  }
}
